import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(true);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const menuItems = [
    {
      title: '사업안내',
      path: '/business/intro',
      subItems: [
        { title: '사업소개', path: '/business/intro' },
      ]
    },
    {
      title: '입지안내',
      path: '/location/environment',
      subItems: [
        { title: '입지환경', path: '/location/environment' },
        { title: '프리미엄', path: '/location/premium' }
      ]
    },
    {
      title: '단지안내',
      path: '/complex/layout',
      subItems: [
        { title: '단지배치도', path: '/complex/layout' },
        { title: '동호수배치도', path: '/complex/building' },
        { title: '커뮤니티', path: '/complex/community' }
      ]
    },
    {
      title: '평형안내',
      path: '/floor-plan/types',
      subItems: [
        { title: '타입안내', path: '/floor-plan/types' }
      ]
    },
    {
      title: '방문예약',
      path: '/reservation',
      subItems: []
    }
  ];

  const isMenuActive = (menu: typeof menuItems[0]) => {
    return menu.subItems.some(sub => location.pathname === sub.path) || location.pathname === menu.path;
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-lg">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center">
              <img
                src="https://static.readdy.ai/image/3b7ebc9e513f68fc027b047fbdd0fbe7/b51d6392ba1debf60a46a5f03e36aae8.png"
                alt="가경 더 플래티넘 센트럴"
                className="h-9 md:h-12 w-auto"
              />
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              {menuItems.map((menu) => (
                <div
                  key={menu.title}
                  className="relative group"
                  onMouseEnter={() => setActiveMenu(menu.title)}
                  onMouseLeave={() => setActiveMenu(null)}
                >
                  <Link
                    to={menu.subItems.length > 0 ? menu.subItems[0].path : menu.path}
                    className={`text-[14px] lg:text-[15px] font-medium transition-colors whitespace-nowrap ${isMenuActive(menu) ? 'text-[#1a2b5c] font-bold' : 'text-gray-800 hover:text-[#1a2b5c]'}`}
                  >
                    {menu.title}
                  </Link>

                  {menu.subItems.length > 0 && (
                    <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 transition-all duration-300 ${activeMenu === menu.title ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
                      <div className="bg-white shadow-xl rounded-lg py-3 px-4 min-w-[140px]">
                        {menu.subItems.map((subItem) => (
                          <Link
                            key={subItem.title}
                            to={subItem.path}
                            className={`block py-2.5 px-3 text-[13px] rounded transition-colors whitespace-nowrap ${location.pathname === subItem.path ? 'text-[#1a2b5c] font-semibold bg-gray-50' : 'text-gray-700 hover:text-[#1a2b5c] hover:bg-gray-50'}`}
                          >
                            {subItem.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <a
                href="tel:1555-5475"
                onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion(); }}
                className="flex items-center gap-1.5 text-[13px] lg:text-[15px] font-medium text-gray-800 hover:text-[#1a2b5c] transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line"></i>
                1555-5475
              </a>
            </div>

            {/* Mobile: phone + hamburger */}
            <div className="flex md:hidden items-center gap-3">
              <a href="tel:1555-5475"
                onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion(); }}
                className="flex items-center justify-center w-9 h-9 text-[#1a2b5c]">
                <i className="ri-phone-line text-xl"></i>
              </a>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="flex items-center justify-center w-9 h-9 text-[#1a2b5c] cursor-pointer"
              >
                <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute top-16 left-0 right-0 bg-white shadow-xl max-h-[calc(100vh-64px)] overflow-y-auto">
            <div className="py-2">
              {menuItems.map((menu) => (
                <div key={menu.title}>
                  {menu.subItems.length > 0 ? (
                    <>
                      <button
                        onClick={() => setMobileExpanded(mobileExpanded === menu.title ? null : menu.title)}
                        className="w-full flex items-center justify-between px-5 py-4 text-[15px] font-semibold text-gray-800 cursor-pointer"
                      >
                        <span className={isMenuActive(menu) ? 'text-[#1a2b5c]' : ''}>{menu.title}</span>
                        <i className={`ri-arrow-${mobileExpanded === menu.title ? 'up' : 'down'}-s-line text-gray-400 text-lg`}></i>
                      </button>
                      {mobileExpanded === menu.title && (
                        <div className="bg-gray-50 border-t border-gray-100">
                          {menu.subItems.map((sub) => (
                            <Link
                              key={sub.path}
                              to={sub.path}
                              className={`block px-8 py-3.5 text-[14px] border-b border-gray-100 last:border-0 ${location.pathname === sub.path ? 'text-[#1a2b5c] font-semibold' : 'text-gray-600'}`}
                            >
                              {sub.title}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={menu.path}
                      className={`block px-5 py-4 text-[15px] font-semibold border-t border-gray-100 ${isMenuActive(menu) ? 'text-[#1a2b5c]' : 'text-gray-800'}`}
                    >
                      {menu.title}
                    </Link>
                  )}
                </div>
              ))}
              <div className="px-5 py-4 border-t border-gray-200">
                <a href="tel:1555-5475"
                  onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion(); }}
                  className="flex items-center gap-2 text-[15px] font-semibold text-[#1a2b5c]">
                  <i className="ri-phone-fill"></i>
                  1555-5475
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
